/**
 * How long an access token has left, read from its `exp` claim — no
 * verification (the server does that), just the public payload.
 *
 * Used by SessionRestorer: a window that opens with a token that is still
 * good (the meeting popup opened from a live session, a reload) does not need
 * to spend a refresh — and every refresh it skips is one fewer rotation for
 * the other windows of the session to catch up with.
 */
export function accessTokenSecondsLeft(token: string | null | undefined, now: number = Date.now()): number | null {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length < 2 || !parts[1]) return null;
  try {
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded)) as { exp?: unknown };
    if (typeof payload.exp !== "number" || !Number.isFinite(payload.exp)) return null;
    return payload.exp - Math.floor(now / 1000);
  } catch {
    return null;
  }
}

/** Fresh enough to skip the cold-start refresh probe. */
export const ACCESS_TOKEN_FRESH_SECONDS = 90;

export function isAccessTokenFresh(token: string | null | undefined, now: number = Date.now()): boolean {
  const left = accessTokenSecondsLeft(token, now);
  return left !== null && left > ACCESS_TOKEN_FRESH_SECONDS;
}
